import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { MapPin, Search } from "lucide-react";

import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Button from "src/components/app/Button";
import AddressFormBottomSheet from "src/components/app/bottom-sheet/AddressFormBottomSheet";
import Input from "src/components/app/Input";
import Loader from "src/components/common/Loader";
import MapView from "src/components/app/map-view/MapView";

import { getAddressComponents } from "src/helpers/common";
import { AddressListResponse } from "src/types/location.types";
import { isInApp } from "src/helpers/parentMessageHandler";
import sendMessageToParent from "src/helpers/parentMessageHandler";

interface LatLng {
  lat: number;
  lng: number;
}

const defaultLocation = {
  lat: 37.4275,
  lng: -122.1697,
};

const AddCompleteAddress: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] =
    useState<LatLng>(defaultLocation);
  const [address, setAddress] = useState({
    pincode: "",
    houseNumber: "",
    street: "",
    formatted_address: "",
    state: "",
    addressId: "",
    address_type: "Home" as "Home" | "Work" | "Other",
  });
  const [isAddressFormOpen, setIsAddressFormOpen] = useState(false);
  const locationContext = useLocation();
  const [isLocationToEdit, setIsLocationToEdit] = useState<boolean>(false);
  const [addressId, setAddressId] = useState<string>("");
  const [addressType, setAddressType] = useState<"Home" | "Work" | "Other">(
    "Home"
  );
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  const fetchAddress = async (
    location: LatLng,
    addressToEdit: AddressListResponse | null
  ) => {
    setIsAddressLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      const data = await response.json();

      if (data.results && data.results[0]) {
        const fullAddress = getAddressComponents(
          data.results[0].address_components
        );
        setAddress({
          addressId: addressId,
          pincode: addressToEdit?.pincode
            ? String(addressToEdit?.pincode)
            : fullAddress.pincode || "",
          houseNumber: fullAddress.houseNumber || "",
          street: addressToEdit?.street || fullAddress.street || "",
          formatted_address:
            addressToEdit?.address || data.results[0].formatted_address || "",
          state: fullAddress.state || "",
          address_type: addressToEdit?.address_type || "Home",
        });
      } else {
        console.error("No results from geocoding API");
        setAddress({
          ...address,
          formatted_address: "Address not found. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setAddress({
        ...address,
        formatted_address: "Error loading address. Please try again.",
      });
    } finally {
      setIsAddressLoading(false);
    }
  };

  const getLocationFromReactNative = () => {
    return new Promise<LatLng>((resolve, reject) => {
      if (isInApp()) {
        // Listen for location response from React Native
        const handleLocationMessage = (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            if (data.action === "getCurrentLocation") {
              if (data.data && data.data.latitude && data.data.longitude) {
                window.removeEventListener("message", handleLocationMessage);
                resolve({
                  lat: data.data.latitude,
                  lng: data.data.longitude,
                });
              } else {
                window.removeEventListener("message", handleLocationMessage);
                reject(new Error("Invalid location data from React Native"));
              }
            }
          } catch (error) {
            window.removeEventListener("message", handleLocationMessage);
            reject(error);
          }
        };

        window.addEventListener("message", handleLocationMessage);

        // Request location from React Native
        sendMessageToParent("getCurrentLocation");

        // Timeout after 10 seconds
        setTimeout(() => {
          window.removeEventListener("message", handleLocationMessage);
          reject(new Error("Location request timeout"));
        }, 10000);
      } else {
        reject(new Error("Not in React Native app"));
      }
    });
  };

  const handleLocationSelect = async (location: LatLng) => {
    let finalLocation,
      addressToEdit = null;
    if (locationContext.state?.searchedLocation) {
      finalLocation = locationContext.state.searchedLocation;
      if (locationContext.state?.isLocationToEdit) {
        setIsLocationToEdit(true);
        setAddressId(locationContext.state.addressId);
        setAddressType(locationContext.state.addressType);
      }
      navigate(locationContext.pathname, { replace: true, state: null });
    } else if (locationContext.state?.locationToEdit) {
      setIsLocationToEdit(true);
      setAddressId(locationContext.state.addressId);
      setAddressType(
        locationContext.state.addressToEdit?.address_type || "Home"
      );
      addressToEdit = locationContext.state.addressToEdit;
      finalLocation = locationContext.state.locationToEdit;
      navigate(locationContext.pathname, { replace: true, state: null });
    } else {
      finalLocation = location;
    }
    setSelectedLocation(finalLocation);
    await fetchAddress(finalLocation, addressToEdit);
  };

  const handleUseCurrentLocation = async () => {
    setIsLocationLoading(true);
    try {
      let userLocation: LatLng;

      if (isInApp()) {
        // Try React Native location first
        try {
          userLocation = await getLocationFromReactNative();
        } catch (error) {
          console.error("Error getting location from React Native:", error);
          console.info(
            "React Native location failed, falling back to browser geolocation"
          );
          userLocation = await getBrowserLocation();
        }
      } else {
        // Use browser geolocation
        userLocation = await getBrowserLocation();
      }

      await handleLocationSelect(userLocation);
    } catch (error) {
      console.error("Error getting location:", error);
      // Set a default location or show error message
      setAddress({
        ...address,
        formatted_address:
          "Unable to get current location. Please select manually.",
      });
    } finally {
      setIsLocationLoading(false);
    }
  };

  const getBrowserLocation = (): Promise<LatLng> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            console.error("Geolocation error:", error);
            reject(error);
          },
          {
            timeout: 10000,
            enableHighAccuracy: true,
            maximumAge: 60000,
          }
        );
      } else {
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="h-screen w-full flex flex-col relative overflow-hidden">
        <Header
          title="Confirm Location"
          onBackClick={() =>
            navigate(`/app/${supportProgramKey}/book-lab-test/checkout`)
          }
        />

        <div className="relative flex-1">
          {selectedLocation && (
            <MapView
              initialLocation={selectedLocation}
              region={selectedLocation}
              onLocationSelect={handleLocationSelect}
              onRegionChange={handleLocationSelect}
              provider="google"
              showsUserLocation
              followsUserLocation
            />
          )}

          <div className="absolute top-4 left-4 right-4 z-20">
            <Input
              name="search"
              type="text"
              placeholder="Search for area, street name..."
              fullWidth
              startAdornment={<Search className="w-4 h-4" />}
              onChange={() => {
                //);
              }}
              onClick={() => {
                navigate(
                  `/app/${supportProgramKey}/book-lab-test/search-address`,
                  {
                    state: {
                      ...(isLocationToEdit && {
                        addressId,
                        addressType,
                        isLocationToEdit,
                        selectedLocation,
                        formatted_address: address.formatted_address,
                      }),
                    },
                  }
                );
              }}
            />
          </div>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <Button
              rounded="full"
              variant="outlined"
              onClick={() => {
                handleUseCurrentLocation();
              }}
              disabled={isLocationLoading}
            >
              {isLocationLoading ? (
                <div className="flex items-center">
                  <Loader size="sm" />
                  <span className="ml-2">Getting location...</span>
                </div>
              ) : (
                <>
                  <MapPin className="w-4 h-4 text-[var(--app-primary)]" />
                  <p className="text-sm pl-2 font-medium text-[var(--app-primary)]">
                    Use Current Location
                  </p>
                </>
              )}
            </Button>
          </div>
        </div>

        {!isAddressFormOpen && (
          <div className="bg-white px-4 pt-4 pb-6 shadow-md rounded-t-2xl z-20">
            {/* Address Row */}
            <div className="flex items-start gap-3">
              {/* Icon aligned with address line */}
              <div className="pt-1">
                <MapPin className="w-5 h-5 text-[var(--app-primary)] flex-shrink-0" />
              </div>

              {/* Address Lines */}
              <div className="flex-1 min-w-0">
                {isAddressLoading ? (
                  <div className="flex items-center">
                    <Loader size="sm" />
                    <span className="ml-2 text-lg font-medium text-gray-900">
                      Loading address...
                    </span>
                  </div>
                ) : (
                  <p className="text-lg font-medium text-gray-900 truncate">
                    {address.formatted_address ||
                      "Select a location on the map"}
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-0.5">
                  {address.state || ""}
                </p>
              </div>
            </div>

            {/* Button */}
            <div className="mt-4">
              <Button
                fullWidth
                rounded="full"
                variant="contained"
                onClick={() => setIsAddressFormOpen(true)}
                disabled={
                  isAddressLoading ||
                  !address.formatted_address ||
                  address.formatted_address.includes("Error") ||
                  address.formatted_address.includes("Unable")
                }
              >
                Add Complete Address
              </Button>
            </div>
          </div>
        )}

        {isAddressFormOpen && (
          <AddressFormBottomSheet
            showBottomSheet={isAddressFormOpen}
            setShowBottomSheet={setIsAddressFormOpen}
            pincode={address.pincode || ""}
            houseNoAndBuilding={address.formatted_address}
            street={address.street || ""}
            addressType={addressType}
            setHouseNoAndBuilding={(houseNoAndBuilding) => {
              setAddress({
                ...address,
                formatted_address: houseNoAndBuilding,
              });
            }}
            lat={selectedLocation.lat}
            lng={selectedLocation.lng}
            isLocationToEdit={isLocationToEdit}
            addressId={addressId || ""}
          />
        )}
      </div>
    </Layout>
  );
};

export default AddCompleteAddress;
